import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import GenericSearchableDropdown from "@ui-components/Dropdown/GenericSearchableDropdown";
import GenericMultiDropdown from "@ui-components/Dropdown/GenericMultiDropdown";
import { useWindowSize } from "@shared/hooks/useWindowSize";
import type {
  ParticipantCandidate,
  ParticipantCandidateTenant,
} from "../request/request";
import {
  describeParticipantsError,
  getCandidateLabel,
  mergeCandidateSelection,
} from "../utils/participants";

interface ParticipantPickerProps {
  /** Customers the backend allows; the picker never works them out itself. */
  candidateTenants: ParticipantCandidateTenant[];
  /** Users of one of those customers. */
  loadCandidates: (tenantId: number) => Promise<ParticipantCandidate[]>;
  selected: ParticipantCandidate[];
  onChange: (selected: ParticipantCandidate[]) => void;
  disabled?: boolean;
  /** Shown when a customer has nobody left to add. */
  emptyItemsMessage: string;
}

/**
 * Pick request participants (CTX-683): a customer, then its users. Picks are
 * kept while switching customers, so people from Mith and from its customer
 * can go in together, and every pick is listed underneath, each removable,
 * since the dropdown trigger only fits the first one and a "+N". Shared by the
 * ticket detail and the create modals (CTX-1256).
 */
export function ParticipantPicker({
  candidateTenants,
  loadCandidates,
  selected,
  onChange,
  disabled = false,
  emptyItemsMessage,
}: ParticipantPickerProps) {
  // On a phone the lists open under their field, inside the page: menus
  // placed by viewport coordinates landed far above the field once the
  // iOS keyboard was up (CTX-1433).
  const viewportMenus = useWindowSize().width >= 640;
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [candidates, setCandidates] = useState<ParticipantCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A single candidate customer needs no choosing.
  const onlyTenantId =
    candidateTenants.length === 1 ? candidateTenants[0].id : null;
  const activeTenantId = tenantId ?? onlyTenantId;

  useEffect(() => {
    if (activeTenantId == null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCandidates([]);
    loadCandidates(activeTenantId)
      .then((users) => {
        if (!cancelled) setCandidates(users);
      })
      .catch((err) => {
        if (!cancelled) setError(describeParticipantsError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTenantId, loadCandidates]);

  const selectedTenantName =
    candidateTenants.find((t) => t.id === activeTenantId)?.name ?? "";
  const selectedEmails = new Set(selected.map((c) => c.email));
  const selectedLabelsHere = candidates
    .filter((c) => selectedEmails.has(c.email))
    .map(getCandidateLabel);

  return (
    <div className="flex flex-col gap-3">
      <div>
        <label className="mb-1 block text-xs text-semantic-text-secondary">
          Customer
        </label>
        <GenericSearchableDropdown
          items={candidateTenants.map((t) => t.name)}
          value={selectedTenantName}
          onSelect={(name) =>
            setTenantId(candidateTenants.find((t) => t.name === name)?.id ?? null)
          }
          defaultLabel="Select a customer"
          allowCustomValues={false}
          disabled={disabled}
          minWidthClass=""
          maxWidthClass=""
          widthClass="w-full"
          fixedPosition={viewportMenus}
          dropdownMaxHeightClass="max-h-48"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs text-semantic-text-secondary">
          Users
        </label>
        <GenericMultiDropdown
          items={candidates.map(getCandidateLabel)}
          value={selectedLabelsHere}
          onSelect={(labels) =>
            onChange(mergeCandidateSelection(selected, candidates, labels))
          }
          defaultLabel={loading ? "Loading users..." : "Select user(s)"}
          emptyItemsMessage={emptyItemsMessage}
          disabled={disabled || activeTenantId == null || loading}
          minWidthClass=""
          maxWidthClass=""
          widthClass="w-full"
          fixedPosition={viewportMenus}
          dropdownMaxHeightClass="max-h-48"
        />
        <p className="mt-1 text-xs text-semantic-text-secondary">
          Anyone without a support portal account gets one when you add them.
        </p>
      </div>

      {selected.length > 0 && (
        <div>
          <p className="mb-1 text-xs text-semantic-text-secondary">
            Selected · {selected.length}
          </p>
          <ul className="flex flex-col gap-1">
            {selected.map((candidate) => (
              <li
                key={candidate.email}
                className="flex items-center gap-2 rounded-md border border-semantic-border-primary bg-semantic-bg-primary px-2 py-1.5"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-semantic-text-primary">
                    {candidate.name || candidate.email}
                  </span>
                  {candidate.name && candidate.name !== candidate.email && (
                    <span className="block truncate text-xs text-semantic-text-secondary">
                      {candidate.email}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    onChange(selected.filter((c) => c.email !== candidate.email))
                  }
                  disabled={disabled}
                  aria-label={`Remove ${candidate.name || candidate.email} from the selection`}
                  title="Remove from the selection"
                  className="flex h-6 w-6 flex-none items-center justify-center rounded text-semantic-text-secondary transition-colors hover:bg-semantic-state-active hover:text-semantic-text-primary"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && <p className="text-xs text-semantic-text-danger">{error}</p>}
    </div>
  );
}

export default ParticipantPicker;
